import { Store } from '@reduxjs/toolkit'
import Axios, { AxiosRequestConfig, AxiosError } from 'axios'
import { default as ServerFormData } from 'form-data'
import { apiUrl, serverApiUrl, xdebugTrigger } from '@fe/common/constants/env'
import { AUTHORIZATION_HEADER } from '@fe/common/constants/headers'
import { formatRequestData } from '@fe/common/utils/axiosUtils'
import { getTokenCookie } from '@fe/common/utils/cookies'
import { isServer } from '@fe/common/utils/nextUtils'

export type RequestConfig = Pick<
  AxiosRequestConfig,
  Exclude<
    keyof AxiosRequestConfig,
    'method' | 'url' | 'data' | 'params' | 'responseType'
  >
>

const buildTransformRequest = (coerceBooleans: boolean) => (data: unknown) => {
  const isFormData = isServer
    ? data instanceof ServerFormData
    : data instanceof FormData

  if (typeof data === 'object' && data !== null && !isFormData) {
    if (Array.isArray(data)) {
      for (const dataElement of data) {
        formatRequestData(dataElement, coerceBooleans)
      }
    } else {
      formatRequestData(data as Record<string, unknown>, coerceBooleans)
    }

    return JSON.stringify(data)
  } else if (typeof data === 'string') {
    return data.trimStart().trimEnd()
  }

  return data
}

/**
 * Boolean-preserving request transform: keeps real JSON `true`/`false` instead
 * of coercing them to 0|1. The FINTWIN applications API validates its
 * confirmation gates against a strict boolean `true`, so those requests use
 * this variant instead of the default transform.
 */
export const transformRequestPreserveBooleans = buildTransformRequest(false)

/**
 * True for a request that never got an HTTP response (connection refused, DNS,
 * timeout, or a cross-origin reply the browser blocked) - axios then rejects
 * with a bare "Network Error" and no `response`. Cancelled requests are not
 * network errors.
 */
export const isNetworkError = (error: unknown): boolean =>
  !!error &&
  !Axios.isCancel(error) &&
  (error as AxiosError).isAxiosError === true &&
  !(error as AxiosError).response

export const configureAxiosInterceptors = (
  store: Store,
  deauthenticate: any,
  // Reporter for requests that died without a response. Such a failure is
  // invisible server-side, so without it nobody learns the API was unreachable.
  onNetworkError?: (error: AxiosError) => void
) => {
  const accessTokenRequestInterceptor = Axios.interceptors.request.use(
    config => {
      const token = getTokenCookie()
      const headers = { ...config.headers }

      if (token) {
        headers[AUTHORIZATION_HEADER] = `Bearer ${token}`
      }

      // Only set Cookie header on server-side to avoid browser forbidden header error
      if (xdebugTrigger && isServer()) {
        const existingCookie = headers['Cookie'] || ''
        const xdebugCookie = `XDEBUG_SESSION=${xdebugTrigger}`
        headers['Cookie'] = existingCookie
          ? `${xdebugCookie}; ${existingCookie}`
          : xdebugCookie
      }

      return {
        ...config,
        headers,
      }
    }
  )

  const responseInterceptor = Axios.interceptors.response.use(
    response => {
      const jsendStatus = response?.data?.status

      if (jsendStatus === 'fail' || jsendStatus === 'error') {
        return Promise.reject(response?.data)
      }

      return response
    },
    (error: AxiosError) => {
      if (error.response?.status === 401 && !isServer()) {
        store.dispatch(deauthenticate())
      }

      if (isNetworkError(error)) {
        onNetworkError?.(error)
      }

      return Promise.reject(error)
    }
  )

  return {
    request: [accessTokenRequestInterceptor],
    response: [responseInterceptor],
  }
}

export const configureAxiosDefaults = () => {
  Axios.defaults.baseURL = (isServer() ? serverApiUrl : apiUrl) + '/v1'
  Axios.defaults.headers['Content-Type'] = 'application/json'
  Axios.defaults.timeout = 60000

  Axios.defaults.transformResponse = (data, headers) => {
    if (
      headers['content-type'].includes('application/json') &&
      typeof data === 'string'
    ) {
      const parsedData = JSON.parse(data)

      const code = parsedData.code
      const status = parsedData.status

      if (
        code &&
        ((code === 422 &&
          Array.isArray(parsedData.data) &&
          !parsedData.data.length) ||
          (!String(code)?.startsWith('2') && code !== 422))
      ) {
        return { message: parsedData.message, status, code }
      }

      if (status === 'fail' || status === 'error') {
        return parsedData
      }

      return parsedData.data || parsedData
    }

    return data
  }

  Axios.defaults.transformRequest = buildTransformRequest(true)
}
